import { requestAvailabilityPrompt } from 'src/hooks/availability/useAvailabilityPrompt.hook';
import { eAvailabilityPromptReason } from 'src/hooks/availability/availabilityPrompt.enum';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLoadedFeatureFlag } from 'src/hooks/featureFlags/useLoadedFeatureFlag.hook';
import type {
  tLeadCloseAbandonVia,
  tLeadCloseFormOpenedProps,
} from 'src/hooks/analytics/leadClose/leadCloseAnalytics.constants';
import { useLeadCloseAnalytics } from 'src/hooks/analytics/leadClose/useLeadCloseAnalytics.hook';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { LEAD_CLOSE_FLAG } from './LeadClose.constants';
import {
  LeadCloseContext,
  tCloseFormState,
  tCloseSuggestion,
  tLastSave,
  tLeadCloseContext,
  tLoad,
} from './LeadClose.context';
import {
  closeErrorReason,
  toEditBody,
  toRegisterBody,
  tCloseForm,
} from './LeadClose.functions';
import { LeadCloseService } from './LeadClose.service';
import LeadCloseDialog from './LeadCloseDialog.component';
import LeadCloseEffects from './LeadCloseEffects.component';
import LeadCloseCelebration from './LeadCloseCelebration.component';
import type {
  tCloseResult,
  tCloseSettings,
  tCloseSummary,
  tCloseType,
  tRegisterAnswer,
} from './LeadClose.types';

const IDLE = { status: 'idle', data: null } as const;
const EMPTY_SUMMARY: tCloseSummary = {
  results: [],
  current: null,
  totals: [],
  prior_amount: null,
  can_create: true,
};

function newId(): string {
  return globalThis.crypto.randomUUID();
}

/**
 * LEA-50's one provider (KTD20). Mounted globally next to WalletProvider and
 * silent without `leads_lead-close`: no flag, no request. It holds, per
 * workspace, the settings and the close summary of each lead it was asked
 * for, the open form and the last confirmed save. Answers are kept under the
 * workspace and lead they were asked for, so a chat or workspace switch never
 * shows another lead's result, and a save answered after its form closed
 * changes nothing on screen.
 */
export default function LeadCloseProvider(props: { children: ReactNode }) {
  const enabled = useLoadedFeatureFlag(LEAD_CLOSE_FLAG);
  const { currentWorkspace } = useWorkspaceContext();
  const workspaceId = currentWorkspace?.id ?? '';
  const workspaceRef = useRef(workspaceId);
  workspaceRef.current = workspaceId;

  const [settingsState, setSettingsState] = useState<{
    workspaceId: string;
    load: tLoad<tCloseSettings>;
  }>({
    workspaceId: '',
    load: IDLE,
  });
  const [settingsTrigger, setSettingsTrigger] = useState(0);
  const [summaries, setSummaries] = useState<
    Record<string, tLoad<tCloseSummary>>
  >({});
  const [form, setForm] = useState<tCloseFormState | null>(null);
  const [lastSave, setLastSave] = useState<tLastSave | null>(null);
  const [suggestion, setSuggestion] = useState<tCloseSuggestion | null>(null);
  const [historyVersion, setHistoryVersion] = useState(0);
  /**
   * Stage mappings this tab set, with the board's copy at that moment. The
   * choice shows until the board's copy changes (our echo or another tab's
   * later mapping); from then on the board wins (U16).
   */
  const [stageOutcomes, setStageOutcomes] = useState<
    Record<string, { value: tCloseType | null; from: tCloseType | null }>
  >({});
  /**
   * When this tab last changed each lead's results: the lead frame that echoes
   * our own save must not cost another summary read (performance goal 0).
   */
  const ownChangesRef = useRef<Record<string, number>>({});

  const formRef = useRef(form);
  formRef.current = form;
  const summariesRef = useRef(summaries);
  summariesRef.current = summaries;
  /**
   * The Idempotency-Key of the open form, the same for every save of it.
   * Minted when a create form opens, so its `form.opened` event can carry it.
   */
  const keyRef = useRef<{ formId: string; key: string } | null>(null);
  const savingRef = useRef(false);
  const { trackFormOpened, trackFormAbandoned } = useLeadCloseAnalytics();
  /**
   * The open create form's `form.opened` props, until it saves or closes: a
   * close without a save reports it abandoned, once. `attempt_id` is the form's
   * key, which every save of the form sends.
   */
  const attemptRef = useRef<{
    formId: string;
    props: tLeadCloseFormOpenedProps;
    openedAt: number;
    failed: boolean;
  } | null>(null);

  useEffect(() => {
    if (!enabled || !workspaceId) return;
    let cancelled = false;
    // A reload of the same workspace keeps the last lists on screen
    // (a settings_conflict must not blank the Settings cards, U17 §4).
    setSettingsState((prev) => ({
      workspaceId,
      load: {
        status: 'loading',
        data: prev.workspaceId === workspaceId ? prev.load.data : null,
      },
    }));
    LeadCloseService.getSettings(workspaceId)
      .then(
        (data) =>
          !cancelled &&
          setSettingsState({ workspaceId, load: { status: 'ready', data } }),
      )
      .catch(
        () =>
          !cancelled &&
          setSettingsState({
            workspaceId,
            load: { status: 'failed', data: null },
          }),
      );
    return () => {
      cancelled = true;
    };
  }, [enabled, workspaceId, settingsTrigger]);

  // A workspace switch drops the open form and the last save with the old workspace.
  useEffect(() => {
    setForm(null);
    setLastSave(null);
    setSuggestion(null);
    keyRef.current = null;
    attemptRef.current = null;
  }, [workspaceId]);

  const settings: tLoad<tCloseSettings> =
    settingsState.workspaceId === workspaceId
      ? settingsState.load
      : enabled
        ? { status: 'loading', data: null }
        : IDLE;

  const summaryKey = useCallback(
    (leadId: string) => `${workspaceId}:${leadId}`,
    [workspaceId],
  );

  const summaryOf = useCallback(
    (leadId: string) => summaries[summaryKey(leadId)] ?? IDLE,
    [summaries, summaryKey],
  );

  const loadSummary = useCallback(
    (leadId: string) => {
      if (!enabled || !workspaceId) return;
      const asked = workspaceId;
      const key = `${asked}:${leadId}`;
      // The panel and the bar ask for the same lead at once: one read serves both.
      if (summariesRef.current[key]?.status === 'loading') return;
      summariesRef.current = {
        ...summariesRef.current,
        [key]: {
          status: 'loading',
          data: summariesRef.current[key]?.data ?? null,
        },
      };
      setSummaries((all) => ({
        ...all,
        [key]: { status: 'loading', data: all[key]?.data ?? null },
      }));
      LeadCloseService.getSummary(asked, leadId)
        .then((data) => {
          if (workspaceRef.current !== asked) return;
          setSummaries((all) => ({ ...all, [key]: { status: 'ready', data } }));
        })
        .catch(() => {
          if (workspaceRef.current !== asked) return;
          setSummaries((all) => ({
            ...all,
            [key]: { status: 'failed', data: all[key]?.data ?? null },
          }));
        });
    },
    [enabled, workspaceId],
  );

  const reloadSettings = useCallback(
    () => setSettingsTrigger((value) => value + 1),
    [],
  );

  const settingsDataRef = useRef<tCloseSettings | null>(null);
  settingsDataRef.current = settings.data;
  /** U17: one PATCH at the held version; a settings_conflict reloads the lists. */
  const saveSettings = useCallback<tLeadCloseContext['saveSettings']>(
    async (body) => {
      const held = settingsDataRef.current;
      const asked = workspaceRef.current;
      if (!held || !asked) return 'unavailable';
      try {
        const data = await LeadCloseService.patchSettings(asked, {
          ...body,
          version: held.version,
        });
        if (workspaceRef.current === asked)
          setSettingsState({
            workspaceId: asked,
            load: { status: 'ready', data },
          });
        return null;
      } catch (error) {
        const reason = closeErrorReason(error) ?? 'unavailable';
        if (reason === 'settings_conflict') reloadSettings();
        return reason;
      }
    },
    [reloadSettings],
  );

  /** Reports the open create form abandoned, once; a save clears it first. */
  const abandon = useCallback(
    (via: tLeadCloseAbandonVia) => {
      const attempt = attemptRef.current;
      attemptRef.current = null;
      if (!attempt) return;
      trackFormAbandoned({
        ...attempt.props,
        via,
        seconds_open: Math.round((Date.now() - attempt.openedAt) / 1000),
        after_error: attempt.failed,
      });
    },
    [trackFormAbandoned],
  );

  const openForm = useCallback<tLeadCloseContext['openForm']>(
    ({ lead, type, source, edit, surface, deferAvailability }) => {
      if (!enabled) return;
      // A form opened over an unsaved one leaves that one behind.
      abandon('other');
      const id = newId();
      setForm({
        surface: surface ?? (source === 'board_drop' ? 'board' : 'chat'),
        deferAvailability,
        id,
        lead,
        type: edit?.type ?? type,
        source,
        saving: false,
        error: null,
        edit,
      });
      // An edit sends no key: it is a replace at a revision (R4).
      keyRef.current = edit ? null : { formId: id, key: newId() };
      if (keyRef.current) {
        const props = {
          type,
          source,
          lead_id: lead.id,
          attempt_id: keyRef.current.key,
        };
        attemptRef.current = {
          formId: id,
          props,
          openedAt: Date.now(),
          failed: false,
        };
        trackFormOpened(props);
      }
      if (summariesRef.current[summaryKey(lead.id)]?.status !== 'ready')
        loadSummary(lead.id);
    },
    [abandon, enabled, loadSummary, summaryKey, trackFormOpened],
  );

  /** The availability prompt a mapped-stage move held back goes once its form closes. */
  const releaseAvailability = useCallback((opened: tCloseFormState | null) => {
    if (opened?.deferAvailability)
      requestAvailabilityPrompt(eAvailabilityPromptReason.LEAD_MOVED);
  }, []);

  const closeForm = useCallback<tLeadCloseContext['closeForm']>(
    (via) => {
      abandon(via ?? 'other');
      releaseAvailability(formRef.current);
      setForm(null);
      keyRef.current = null;
    },
    [abandon, releaseAvailability],
  );

  /** Edit mode: a full replace at the revision the row was read at (R4). */
  const submitEdit = useCallback(
    async (
      opened: tCloseFormState,
      result: tCloseResult,
      values: tCloseForm,
      summary: tCloseSummary,
      stillOpen: () => boolean,
    ): Promise<null> => {
      const asked = workspaceRef.current;
      try {
        await LeadCloseService.edit(
          asked,
          opened.lead.id,
          result.uuid,
          toEditBody(values, {
            summary: { ...summary, prior_amount: result.prior_amount },
            revision: result.revision,
          }),
        );
        ownChangesRef.current[opened.lead.id] = Date.now();
        loadSummary(opened.lead.id);
        setHistoryVersion((value) => value + 1);
        if (stillOpen()) {
          setForm(null);
          setLastSave({
            id: newId(),
            kind: 'edit',
            leadId: opened.lead.id,
            leadName: opened.lead.name,
            source: opened.source,
            surface: opened.surface,
          });
        }
      } catch (error) {
        const reason = closeErrorReason(error);
        // The row changed or went away: read what is there now and show it,
        // so the next try is made at the current revision (AE9).
        if (reason === 'revision_conflict' || reason === 'result_deleted') {
          setHistoryVersion((value) => value + 1);
          try {
            const fresh = await LeadCloseService.getSummary(
              asked,
              opened.lead.id,
            );
            setSummaries((all) => ({
              ...all,
              [`${asked}:${opened.lead.id}`]: { status: 'ready', data: fresh },
            }));
            const current = fresh.results.find(
              (r) => r.uuid === result.uuid && !r.deleted_at,
            );
            if (current && stillOpen()) {
              setForm((held) =>
                held?.id === opened.id ? { ...held, edit: current } : held,
              );
            }
          } catch {
            loadSummary(opened.lead.id);
          }
        }
        if (stillOpen()) {
          setForm((current) =>
            current?.id === opened.id
              ? { ...current, saving: false, error: reason ?? 'unavailable' }
              : current,
          );
        }
      } finally {
        savingRef.current = false;
      }
      return null;
    },
    [loadSummary],
  );

  const removeResult = useCallback<tLeadCloseContext['removeResult']>(
    async (leadId, result) => {
      const asked = workspaceRef.current;
      ownChangesRef.current[leadId] = Date.now();
      try {
        await LeadCloseService.remove(
          asked,
          leadId,
          result.uuid,
          result.revision,
        );
        ownChangesRef.current[leadId] = Date.now();
        return null;
      } catch (error) {
        return closeErrorReason(error) ?? 'unavailable';
      } finally {
        loadSummary(leadId);
        setHistoryVersion((value) => value + 1);
      }
    },
    [loadSummary],
  );

  const dismissSuggestion = useCallback(() => setSuggestion(null), []);

  /** Mappings the board's copy has overtaken: from then on the board wins for good. */
  const overtakenRef = useRef(new Set<string>());
  const stageOutcomeOf = useCallback<tLeadCloseContext['stageOutcomeOf']>(
    (stageId, held) => {
      const mine = stageOutcomes[stageId];
      const board = held ?? null;
      if (!mine || overtakenRef.current.has(stageId)) return board;
      if (mine.from !== board) {
        // Our echo or a later change arrived: never fall back to ours (A→B→A).
        overtakenRef.current.add(stageId);
        return board;
      }
      return mine.value;
    },
    [stageOutcomes],
  );

  const mapStage = useCallback<tLeadCloseContext['mapStage']>(
    async (funnelId, stageId, value, held) => {
      const mine = { value, from: held ?? null };
      overtakenRef.current.delete(stageId);
      let previous: (typeof stageOutcomes)[string] | undefined;
      setStageOutcomes((all) => {
        previous = all[stageId];
        return { ...all, [stageId]: mine };
      });
      try {
        await LeadCloseService.setStageOpensOutcome(
          workspaceRef.current,
          funnelId,
          stageId,
          value,
        );
        return null;
      } catch (error) {
        // Only this stage, and only if nothing newer replaced this choice.
        setStageOutcomes((all) => {
          if (all[stageId] !== mine) return all;
          const rest = { ...all };
          if (previous) rest[stageId] = previous;
          else delete rest[stageId];
          return rest;
        });
        return closeErrorReason(error) ?? 'unavailable';
      }
    },
    [],
  );

  const submitForm = useCallback(
    async (values: tCloseForm): Promise<tRegisterAnswer | null> => {
      const opened = formRef.current;
      if (!opened || savingRef.current) return null;
      savingRef.current = true;
      const asked = workspaceId;
      if (keyRef.current?.formId !== opened.id)
        keyRef.current = { formId: opened.id, key: newId() };
      const key = keyRef.current.key;
      const summary =
        summariesRef.current[`${asked}:${opened.lead.id}`]?.data ??
        EMPTY_SUMMARY;
      const stillOpen = () =>
        formRef.current?.id === opened.id && workspaceRef.current === asked;
      setForm((current) =>
        current?.id === opened.id
          ? { ...current, saving: true, error: null }
          : current,
      );
      // Stamped before the request: the lead frame of our own save can arrive
      // before its answer (leads-service announces after commit, bff-web
      // archives after the save), and must cost no summary read.
      ownChangesRef.current[opened.lead.id] = Date.now();
      if (opened.edit) {
        return submitEdit(opened, opened.edit, values, summary, stillOpen);
      }
      try {
        const answer = await LeadCloseService.register(
          asked,
          opened.lead.id,
          toRegisterBody(values, { summary, source: opened.source }),
          key,
        );
        if (workspaceRef.current === asked) {
          const summaryId = `${asked}:${opened.lead.id}`;
          setSummaries((all) => {
            const held = all[summaryId]?.data ?? EMPTY_SUMMARY;
            const data = {
              ...held,
              // A read that landed first may already list it.
              results: [
                answer.result,
                ...held.results.filter((r) => r.uuid !== answer.result.uuid),
              ],
              current: answer.result,
              totals: answer.totals,
              prior_amount: answer.prior_amount,
            };
            return { ...all, [summaryId]: { status: 'ready', data } };
          });
        }
        ownChangesRef.current[opened.lead.id] = Date.now();
        // Saved: this form is never reported abandoned.
        if (attemptRef.current?.formId === opened.id) attemptRef.current = null;
        if (stillOpen()) {
          keyRef.current = null;
          setForm(null);
          setLastSave({
            id: newId(),
            kind: 'create',
            leadId: opened.lead.id,
            leadName: opened.lead.name,
            answer,
            source: opened.source,
            surface: opened.surface,
          });
          releaseAvailability(opened);
          // The chat suggests the stage the answer names; the board shows its
          // own notice (U22) and an archived lead leaves the conversation.
          const chat =
            opened.source === 'chat_bar' || opened.source === 'lead_panel';
          const archived =
            answer.archive_status === 'archived' ||
            answer.archive_status === 'already_archived';
          setSuggestion(
            chat && !archived && answer.suggested_stage
              ? {
                  leadId: opened.lead.id,
                  leadName: opened.lead.name,
                  stage: answer.suggested_stage,
                }
              : null,
          );
        }
        return answer;
      } catch (error) {
        const reason = closeErrorReason(error);
        if (attemptRef.current?.formId === opened.id)
          attemptRef.current.failed = true;
        // The key never rotates inside a form, so `lead.close.registered`
        // always joins `form.opened` by `attempt_id`. A refusal stores nothing
        // under it, so the corrected save reuses it. A conflict means an
        // earlier save of this form already went through: a new key would
        // register it twice, so the summary is read again to show it.
        // Something changed under the form: read it again so the next try
        // is made against what is there now.
        if (
          reason === 'currency_not_allowed' ||
          reason === 'option_unavailable' ||
          reason === 'other_not_allowed'
        ) {
          reloadSettings();
        }
        if (
          reason === 'prior_amount_changed' ||
          reason === 'idempotency_conflict'
        )
          loadSummary(opened.lead.id);
        if (stillOpen()) {
          setForm((current) =>
            current?.id === opened.id
              ? { ...current, saving: false, error: reason ?? 'unavailable' }
              : current,
          );
        }
        return null;
      } finally {
        savingRef.current = false;
      }
    },
    [loadSummary, reloadSettings, releaseAvailability, submitEdit, workspaceId],
  );

  const value = useMemo<tLeadCloseContext>(
    () => ({
      enabled,
      settings,
      summaryOf,
      loadSummary,
      refreshSummary: loadSummary,
      reloadSettings,
      form,
      openForm,
      closeForm,
      submitForm,
      removeResult,
      lastSave,
      suggestion,
      dismissSuggestion,
      historyVersion,
      stageOutcomeOf,
      mapStage,
      saveSettings,
    }),
    [
      enabled,
      settings,
      summaryOf,
      loadSummary,
      reloadSettings,
      form,
      openForm,
      closeForm,
      submitForm,
      removeResult,
      lastSave,
      suggestion,
      dismissSuggestion,
      historyVersion,
      stageOutcomeOf,
      mapStage,
      saveSettings,
    ],
  );

  return (
    <LeadCloseContext.Provider value={value}>
      {props.children}
      {enabled ? (
        <>
          <LeadCloseDialog />
          <LeadCloseEffects ownChanges={ownChangesRef} />
          <LeadCloseCelebration />
        </>
      ) : null}
    </LeadCloseContext.Provider>
  );
}
